// Canonical brand visual constants for runtime contexts that cannot
// import from globals.css (email HTML, satori OG images, error pages).
// Mirror the values in src/app/globals.css @theme block.
// Sole exception to the hex-literal pre-commit hook.

export const BRAND_COLORS = {
  primary: '#0f172a',       // slate-900
  secondary: '#475569',     // slate-600
  tertiary: '#94a3b8',      // slate-400
  cta: '#047857',           // emerald-700: canonical CTA, WCAG-AA 4.84:1 on white
  ctaHover: '#065f46',      // emerald-800
  ctaActive: '#064e3b',     // emerald-900
  surface0: '#ffffff',
  surface1: '#f8fafc',      // slate-50
  surface2: '#f1f5f9',      // slate-100
  border: '#e2e8f0',        // slate-200
  borderStrong: '#cbd5e1',  // slate-300
  signalGreen: '#16a34a',   // green-600
  signalAmber: '#f59e0b',   // amber-500
  signalRose: '#f43f5e',    // rose-500
} as const;

// OG image palette -- for satori/ImageResponse files that cannot use CSS vars.
// These match the original design but use the canonical design-token values.
export const OG_PALETTE = {
  bg: '#fdfcfa',            // warm off-white page background
  white: '#ffffff',
  nearBlack: '#18181b',     // zinc-900 -- logo/heading in OG images
  muted: '#71717a',         // zinc-500 -- secondary OG text
  mutedLight: '#a1a1aa',    // zinc-400 -- tertiary labels
  mutedDark: '#52525b',     // zinc-600 -- price values
  border: '#e4e4e7',        // zinc-200 -- OG border
  surface: '#f4f4f5',       // zinc-100 -- OG badge bg
  cta: '#047857',           // emerald-700 -- canonical CTA (was emerald-500 pre-F2)
  ctaHover: '#065f46',      // emerald-800
  ctaDark: '#047857',       // emerald-700
  ctaDarkest: '#064e3b',    // emerald-900 -- SVG label fill
  emeraldBg: '#ecfdf5',     // emerald-50
} as const;

// Legacy email palette used in older route.ts HTML templates.
// These zinc-based colours pre-date the slate-based design system.
export const EMAIL_COLORS = {
  heading: '#0f172a',       // slate-900 -- email h1 (was zinc-950 pre-F2)
  body: '#3f3f46',          // zinc-700 -- email body text
  muted: '#a1a1aa',         // zinc-400 -- email footer / secondary
  cta: '#047857',           // emerald-700 -- canonical CTA button bg (was emerald-500 pre-F2)
  ctaText: '#fff',
  border: '#e4e4e7',        // zinc-200 -- email hr
} as const;

// SVG hero chart palette -- for BlogHeroB.tsx SVG fill/stroke attributes
// which cannot use CSS custom properties.
// Hex values are required here; CSS vars are not supported inside SVG attrs.
export const HERO_PALETTE = {
  white: '#ffffff',
  // Emerald scale
  e300: '#6ee7b7',
  e400: '#34d399',
  e500: '#10b981',
  e600: '#059669',
  e700: '#047857',
  e800: '#065f46',
  // Emerald tints
  e100: '#d1fae5',
  e200: '#a7f3d0',
  // Grey scale (zinc)
  z200: '#e4e4e7',
  z100: '#f4f4f5',
  // Neutral axis lines (zinc-300 area)
  axis: '#d4d4d8',
  // Text greys
  g400: '#9ca3af',
  g500: '#6b7280',
  g700: '#374151',
} as const;

export const BRAND_FONT = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
export const BRAND_FONT_DISPLAY = "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";
